/*
  Admin Packages dashboard.
  ------------------------------------------------------------------
  Mirrors the in-app-purchase screen on mobile but in a flat table so
  admins can scan every platform/lang combination side-by-side. The
  selected language acts as a "preview lens": each row's name,
  description and package_title are shown in that language, falling
  back to the base Package row when no PackageTranslation exists yet.

  Edits open a drawer-style dialog with shared fields + the localized
  fields for the currently selected language; deletes are soft (the
  Package model is paranoid) and the confirmation copy is explicit
  about the fact that this hides the package in ALL languages on every
  platform, because under the hood there's a single Package row.
*/

import { useEffect, useMemo, useState } from "react";

import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import LanguageIcon from "@mui/icons-material/Language";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { openAppConfirm, openAppAlert } from "components/AppDialogs";

import {
  GetAdminPackagesCall,
  AdminEditPackageCall,
  AdminDeletePackageCall,
} from "../../redux/api/package.api";

/*
  Language choices match the rest of the admin UI (see quizTitles +
  pushNotificationa) so the filter is consistent across pages. "en"
  is the implicit base language for most Package rows.
*/
const LANGUAGE_OPTIONS = [
  { code: "en", label: "English" },
  { code: "fr", label: "French" },
  { code: "es", label: "Spanish" },
  { code: "it", label: "Italian" },
  { code: "pt", label: "Portuguese (Portugal)" },
  { code: "pt-br", label: "Portuguese (Brazil)" },
  { code: "ge", label: "German" },
  { code: "nl", label: "Dutch" },
  { code: "ar", label: "Arabic" },
  { code: "tr", label: "Turkish" },
  { code: "ru", label: "Russian" },
  { code: "ja", label: "Japanese" },
  { code: "ko", label: "Korean" },
  { code: "zh-cn", label: "Chinese (Simplified)" },
  { code: "zh-tw", label: "Chinese (Traditional)" },
  { code: "cn", label: "Chinese (legacy code)" },
  { code: "hi", label: "Hindi" },
  { code: "fa", label: "Persian (Farsi)" },
  { code: "id", label: "Indonesian" },
  { code: "ro", label: "Romanian" },
  { code: "vi", label: "Vietnamese" },
  { code: "ms", label: "Malay" },
  { code: "tl", label: "Tagalog (Filipino)" },
  { code: "no", label: "Norwegian" },
  { code: "da", label: "Danish" },
  { code: "sv", label: "Swedish" },
  { code: "el", label: "Greek" },
  { code: "pl", label: "Polish" },
  { code: "cs", label: "Czech" },
  { code: "fi", label: "Finnish" },
  { code: "hu", label: "Hungarian" },
  { code: "kk", label: "Kazakh" },
  { code: "tk", label: "Turkmen" },
];

const PLATFORM_OPTIONS = [
  { value: "ios", label: "iOS" },
  { value: "android", label: "Android" },
  { value: "web", label: "Web" },
  { value: "ios_web", label: "iOS + Web" },
  { value: "android_web", label: "Android + Web" },
  { value: "ios_android_web", label: "iOS + Android + Web" },
];

const PLATFORM_LABEL = PLATFORM_OPTIONS.reduce((acc, p) => {
  acc[p.value] = p.label;
  return acc;
}, {});

function platformChipColor(type) {
  switch (type) {
    case "ios":
      return "default";
    case "android":
      return "success";
    case "web":
      return "info";
    case "ios_web":
      return "primary";
    case "android_web":
      return "warning";
    case "ios_android_web":
      return "secondary";
    default:
      return "default";
  }
}

/*
  Returns the localized view of a package for a given lang:
  prefers a matching PackageTranslation, falls back to the base
  Package row. Mirrors the resolution the mobile app uses, see
  payment.controller.js#getPackage.
*/
function resolveLocale(pkg, lang) {
  const base = {
    name: pkg.name || "",
    description: pkg.description || "",
    package_title: pkg.package_title || "",
  };
  if (!lang || lang === pkg.base_lang) return { ...base, source: "base" };
  const t = (pkg.translations || []).find((x) => x.lang === lang);
  if (!t) return { ...base, source: "fallback" };
  return {
    name: t.name || base.name,
    description: t.description || base.description,
    package_title: t.package_title || base.package_title,
    source: "translation",
  };
}

function expireLimitLabel(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return "—";
  if (n % 365 === 0) return `${n / 365} year${n / 365 > 1 ? "s" : ""}`;
  if (n % 30 === 0) return `${n / 30} month${n / 30 > 1 ? "s" : ""}`;
  if (n % 7 === 0) return `${n / 7} week${n / 7 > 1 ? "s" : ""}`;
  return `${n} day${n > 1 ? "s" : ""}`;
}

const headCellSx = {
  py: 2,
  px: 2,
  fontSize: "0.75rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "text.secondary",
  borderBottom: "1px solid",
  borderColor: "divider",
  bgcolor: "grey.50",
};

const bodyCellSx = {
  py: 2,
  px: 2,
  fontSize: "0.875rem",
  verticalAlign: "top",
  borderColor: "divider",
};

export default function PackagesPage() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshTick, setRefreshTick] = useState(0);

  const [previewLang, setPreviewLang] = useState("en");
  const [platformFilter, setPlatformFilter] = useState("");

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuRow, setMenuRow] = useState(null);

  const [editing, setEditing] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    GetAdminPackagesCall().then(({ packages: pkgs }) => {
      if (cancelled) return;
      setPackages(Array.isArray(pkgs) ? pkgs : []);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [refreshTick]);

  /*
    Filtering by platform happens entirely on the client because the
    catalog is small (<= a few dozen rows). Sorting by platform then
    expire_limit is already applied server-side; we leave that order
    intact for stability.
  */
  const visibleRows = useMemo(() => {
    if (!platformFilter) return packages;
    return packages.filter((p) => p.type === platformFilter);
  }, [packages, platformFilter]);

  const totalCount = packages.length;
  const visibleCount = visibleRows.length;

  const openMenu = (event, row) => {
    setMenuAnchor(event.currentTarget);
    setMenuRow(row);
  };
  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuRow(null);
  };

  const onEditClick = () => {
    if (!menuRow) return;
    setEditing(menuRow);
    closeMenu();
  };

  const onDeleteClick = async () => {
    const target = menuRow;
    closeMenu();
    if (!target) return;
    const titleForConfirm =
      resolveLocale(target, previewLang).name ||
      target.name ||
      target.product_id ||
      "this package";
    const ok = await openAppConfirm({
      title: "Soft-delete package?",
      message:
        `“${titleForConfirm}” will be hidden in ALL languages on every ` +
        `platform that this package serves (${PLATFORM_LABEL[target.type] || target.type}). ` +
        `The row stays in the database (paranoid soft delete) so it can be restored later by a developer.`,
      confirmText: "Delete in all languages",
      cancelText: "Keep package",
      confirmColor: "error",
    });
    if (!ok) return;
    const result = await AdminDeletePackageCall(target.id);
    if (result.ok) {
      setRefreshTick((t) => t + 1);
    } else {
      openAppAlert({
        title: "Could not delete",
        message: result.message || "Please try again.",
        color: "error",
      });
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox
        component="main"
        sx={{
          py: { xs: 3, md: 4 },
          px: { xs: 2, sm: 3, md: 4 },
          maxWidth: 1600,
          mx: "auto",
        }}
      >
        <Stack spacing={2.5} sx={{ mb: 3 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "flex-start", md: "flex-end" }}
            justifyContent="space-between"
          >
            <Box>
              <MDTypography
                variant="h4"
                fontWeight="bold"
                color="text"
                sx={{ letterSpacing: "-0.02em" }}
              >
                Packages
              </MDTypography>
              <MDTypography
                variant="body1"
                color="text"
                sx={{ mt: 1, maxWidth: 820, lineHeight: 1.6, opacity: 0.85 }}
              >
                The exact catalog the mobile in-app-purchase screen consumes —
                across iOS, Android and Web. Pick a language to preview how each
                package reads in that locale, then use the 3-dot menu to edit
                copy & pricing or soft-delete a package across all languages.
              </MDTypography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {loading
                  ? "Loading…"
                  : platformFilter
                  ? `${visibleCount} of ${totalCount} packages match this filter`
                  : `${totalCount} ${totalCount === 1 ? "package" : "packages"}`}
              </Typography>
            </Box>

            <Stack direction="row" spacing={1.5} alignItems="center">
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel id="pkg-lang-filter">Language</InputLabel>
                <Select
                  labelId="pkg-lang-filter"
                  label="Language"
                  value={previewLang}
                  onChange={(e) => setPreviewLang(e.target.value)}
                  startAdornment={
                    <LanguageIcon
                      fontSize="small"
                      sx={{ mr: 1, color: "text.secondary" }}
                    />
                  }
                  sx={{ height: 40 }}
                >
                  {LANGUAGE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.code} value={opt.code}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel id="pkg-platform-filter">Platform</InputLabel>
                <Select
                  labelId="pkg-platform-filter"
                  label="Platform"
                  value={platformFilter}
                  onChange={(e) => setPlatformFilter(e.target.value)}
                  sx={{ height: 40 }}
                >
                  <MenuItem value="">All platforms</MenuItem>
                  {PLATFORM_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Tooltip title="Refresh">
                <IconButton
                  onClick={() => setRefreshTick((t) => t + 1)}
                  size="small"
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                  }}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>

          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              overflow: "hidden",
              boxShadow: (theme) =>
                theme.palette.mode === "dark"
                  ? "0 8px 32px rgba(0,0,0,0.35)"
                  : "0 12px 40px rgba(15, 23, 42, 0.06)",
            }}
          >
            <TableContainer>
              {loading ? (
                <Box display="flex" justifyContent="center" py={8}>
                  <CircularProgress size={36} />
                </Box>
              ) : (
                <Table size="medium" sx={{ minWidth: 1100 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...headCellSx, minWidth: 280 }}>
                        Package
                      </TableCell>
                      <TableCell sx={headCellSx}>Platform</TableCell>
                      <TableCell sx={headCellSx}>Price</TableCell>
                      <TableCell sx={headCellSx}>Duration</TableCell>
                      <TableCell sx={{ ...headCellSx, minWidth: 220 }}>
                        Store identifiers
                      </TableCell>
                      <TableCell sx={headCellSx}>Languages</TableCell>
                      <TableCell sx={{ ...headCellSx, width: 64 }} align="right">
                        {" "}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} sx={{ ...bodyCellSx, py: 8 }}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            align="center"
                          >
                            No packages match this filter.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      visibleRows.map((row) => {
                        const locale = resolveLocale(row, previewLang);
                        const isFallback =
                          previewLang !== row.base_lang &&
                          locale.source !== "translation";
                        const langCount =
                          (row.translations?.length || 0) + 1; // +1 for base lang
                        return (
                          <TableRow
                            key={row.id}
                            hover
                            sx={{ "&:last-child td": { borderBottom: 0 } }}
                          >
                            <TableCell sx={{ ...bodyCellSx, maxWidth: 380 }}>
                              <Stack spacing={0.5}>
                                {locale.package_title ? (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                      textTransform: "uppercase",
                                      letterSpacing: "0.06em",
                                      fontWeight: 700,
                                    }}
                                  >
                                    {locale.package_title}
                                  </Typography>
                                ) : null}
                                <Typography
                                  variant="body2"
                                  fontWeight={700}
                                  sx={{
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                    wordBreak: "break-word",
                                  }}
                                  title={locale.name}
                                >
                                  {locale.name || "(no name)"}
                                </Typography>
                                {isFallback ? (
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    color="warning"
                                    label={`No ${previewLang.toUpperCase()} translation — showing ${row.base_lang?.toUpperCase()}`}
                                    sx={{
                                      fontWeight: 500,
                                      alignSelf: "flex-start",
                                      maxWidth: "100%",
                                    }}
                                  />
                                ) : null}
                              </Stack>
                            </TableCell>
                            <TableCell sx={bodyCellSx}>
                              <Chip
                                size="small"
                                label={PLATFORM_LABEL[row.type] || row.type || "—"}
                                color={platformChipColor(row.type)}
                                variant="outlined"
                                sx={{ fontWeight: 600 }}
                              />
                            </TableCell>
                            <TableCell sx={bodyCellSx}>
                              <Typography variant="body2" fontWeight={600}>
                                {row.price ? `€${row.price}` : "—"}
                              </Typography>
                            </TableCell>
                            <TableCell sx={bodyCellSx}>
                              <Typography variant="body2" color="text.secondary">
                                {expireLimitLabel(row.expire_limit)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {row.expire_limit} days
                              </Typography>
                            </TableCell>
                            <TableCell sx={bodyCellSx}>
                              <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    textTransform: "uppercase",
                                    letterSpacing: "0.06em",
                                    fontWeight: 700,
                                  }}
                                >
                                  product_id
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontFamily:
                                      "ui-monospace, Menlo, Consolas, monospace",
                                    fontSize: 12,
                                    wordBreak: "break-all",
                                  }}
                                >
                                  {row.product_id || "—"}
                                </Typography>
                                {row.identifier ? (
                                  <>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{
                                        textTransform: "uppercase",
                                        letterSpacing: "0.06em",
                                        fontWeight: 700,
                                        mt: 0.5,
                                      }}
                                    >
                                      identifier
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontFamily:
                                          "ui-monospace, Menlo, Consolas, monospace",
                                        fontSize: 12,
                                        wordBreak: "break-all",
                                      }}
                                    >
                                      {row.identifier}
                                    </Typography>
                                  </>
                                ) : null}
                              </Stack>
                            </TableCell>
                            <TableCell sx={bodyCellSx}>
                              <Stack spacing={0.5}>
                                <Chip
                                  size="small"
                                  label={`${langCount} ${
                                    langCount === 1 ? "lang" : "langs"
                                  }`}
                                  variant="filled"
                                  sx={{ alignSelf: "flex-start" }}
                                />
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    label={row.base_lang?.toUpperCase() || "—"}
                                    sx={{ fontWeight: 600 }}
                                  />
                                  {(row.translations || [])
                                    .slice(0, 6)
                                    .map((t) => (
                                      <Chip
                                        key={t.id || t.lang}
                                        size="small"
                                        variant="outlined"
                                        label={t.lang?.toUpperCase()}
                                      />
                                    ))}
                                  {(row.translations || []).length > 6 ? (
                                    <Chip
                                      size="small"
                                      variant="outlined"
                                      label={`+${
                                        (row.translations || []).length - 6
                                      }`}
                                    />
                                  ) : null}
                                </Box>
                              </Stack>
                            </TableCell>
                            <TableCell align="right" sx={bodyCellSx}>
                              <Tooltip title="Actions">
                                <IconButton
                                  size="small"
                                  onClick={(e) => openMenu(e, row)}
                                >
                                  <MoreVertIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              )}
            </TableContainer>
          </Paper>
        </Stack>

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={closeMenu}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        >
          <MenuItem onClick={onEditClick}>
            <EditIcon fontSize="small" sx={{ mr: 1.2 }} />
            Edit package
          </MenuItem>
          <MenuItem onClick={onDeleteClick} sx={{ color: "error.main" }}>
            <DeleteIcon fontSize="small" sx={{ mr: 1.2 }} />
            Soft delete (all languages)
          </MenuItem>
        </Menu>

        {editing ? (
          <EditPackageDialog
            initialPackage={editing}
            initialLang={previewLang}
            onClose={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              setRefreshTick((t) => t + 1);
            }}
          />
        ) : null}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

/*
  Edit dialog. The drawer is split into two sections:

    1. Shared fields — these apply to every language because they
       live on the Package row itself (price, store ids, platform,
       duration, discount code).
    2. Per-language fields — a small tab strip lets the admin
       switch between languages without leaving the modal. Each tab
       edits one PackageTranslation row (or the base Package row
       when the tab matches Package.lang).

  We always keep the base language as the first tab so admins are
  reminded that the "fallback" copy lives there.
*/
function EditPackageDialog({ initialPackage, initialLang, onClose, onSaved }) {
  const baseLang = initialPackage.base_lang || "en";

  const allLangs = useMemo(() => {
    const fromTranslations = (initialPackage.translations || []).map(
      (t) => t.lang,
    );
    const set = new Set([baseLang, ...fromTranslations]);
    if (initialLang) set.add(initialLang);
    return Array.from(set);
  }, [initialPackage, baseLang, initialLang]);

  const [shared, setShared] = useState({
    price: initialPackage.price || "",
    type: initialPackage.type || "ios",
    product_id: initialPackage.product_id || "",
    expire_limit: initialPackage.expire_limit ?? "",
    identifier: initialPackage.identifier || "",
    discount_code: initialPackage.discount_code || "",
  });

  // Local edits to per-language fields, keyed by lang.
  const [localized, setLocalized] = useState(() => {
    const seed = {};
    seed[baseLang] = {
      name: initialPackage.name || "",
      description: initialPackage.description || "",
      package_title: initialPackage.package_title || "",
    };
    (initialPackage.translations || []).forEach((t) => {
      seed[t.lang] = {
        name: t.name || "",
        description: t.description || "",
        package_title: t.package_title || "",
      };
    });
    if (initialLang && !seed[initialLang]) {
      seed[initialLang] = { name: "", description: "", package_title: "" };
    }
    return seed;
  });

  const [activeTab, setActiveTab] = useState(() => {
    if (initialLang && allLangs.includes(initialLang)) return initialLang;
    return baseLang;
  });
  const [saving, setSaving] = useState(false);

  const [extraLangPicker, setExtraLangPicker] = useState(false);
  const [extraLangChoice, setExtraLangChoice] = useState("fr");

  const addLanguageTab = () => {
    if (!extraLangChoice) return;
    setLocalized((prev) => {
      if (prev[extraLangChoice]) return prev;
      return {
        ...prev,
        [extraLangChoice]: { name: "", description: "", package_title: "" },
      };
    });
    setActiveTab(extraLangChoice);
    setExtraLangPicker(false);
  };

  const updateLocalizedField = (lang, field, value) => {
    setLocalized((prev) => ({
      ...prev,
      [lang]: { ...(prev[lang] || {}), [field]: value },
    }));
  };

  const updateSharedField = (field, value) => {
    setShared((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    const translations = Object.keys(localized).map((lang) => ({
      lang,
      ...localized[lang],
    }));
    const body = {
      ...shared,
      expire_limit:
        shared.expire_limit === "" || shared.expire_limit == null
          ? undefined
          : Number(shared.expire_limit),
      translations,
    };
    const result = await AdminEditPackageCall(initialPackage.id, body);
    setSaving(false);
    if (result.ok) {
      onSaved?.();
    } else {
      openAppAlert({
        title: "Could not save",
        message: result.message || "Please try again.",
        color: "error",
      });
    }
  };

  const tabLangs = useMemo(() => {
    const set = new Set(Object.keys(localized));
    set.add(baseLang);
    return Array.from(set).sort((a, b) => {
      if (a === baseLang) return -1;
      if (b === baseLang) return 1;
      return a.localeCompare(b);
    });
  }, [localized, baseLang]);

  const currentLocal = localized[activeTab] || {
    name: "",
    description: "",
    package_title: "",
  };

  return (
    <Dialog
      open
      onClose={saving ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <span>Edit package</span>
          <Chip
            size="small"
            label={PLATFORM_LABEL[initialPackage.type] || initialPackage.type}
            sx={{ ml: 1 }}
          />
        </Stack>
        <Typography variant="caption" color="text.secondary">
          product_id: {initialPackage.product_id}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          <Box>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ fontWeight: 700 }}
            >
              Shared fields (apply to every language)
            </Typography>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mt: 1 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="edit-platform">Platform</InputLabel>
                <Select
                  labelId="edit-platform"
                  label="Platform"
                  value={shared.type}
                  onChange={(e) => updateSharedField("type", e.target.value)}
                >
                  {PLATFORM_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                size="small"
                label="Price (€)"
                value={shared.price}
                onChange={(e) => updateSharedField("price", e.target.value)}
              />
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Duration (days)"
                value={shared.expire_limit}
                onChange={(e) => updateSharedField("expire_limit", e.target.value)}
                helperText={expireLimitLabel(shared.expire_limit)}
              />
            </Stack>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mt: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="Store product_id"
                value={shared.product_id}
                onChange={(e) => updateSharedField("product_id", e.target.value)}
              />
              <TextField
                fullWidth
                size="small"
                label="RevenueCat identifier"
                value={shared.identifier}
                onChange={(e) => updateSharedField("identifier", e.target.value)}
              />
              <TextField
                fullWidth
                size="small"
                label="Discount code"
                value={shared.discount_code}
                onChange={(e) => updateSharedField("discount_code", e.target.value)}
                helperText='Use "all" for the default catalog'
              />
            </Stack>
          </Box>

          <Divider />

          <Box>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="space-between"
              sx={{ flexWrap: "wrap" }}
            >
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ fontWeight: 700 }}
              >
                Language-specific copy
              </Typography>
              {extraLangPicker ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel id="add-lang">Add language</InputLabel>
                    <Select
                      labelId="add-lang"
                      label="Add language"
                      value={extraLangChoice}
                      onChange={(e) => setExtraLangChoice(e.target.value)}
                    >
                      {LANGUAGE_OPTIONS.filter(
                        (l) => !tabLangs.includes(l.code),
                      ).map((opt) => (
                        <MenuItem key={opt.code} value={opt.code}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <MDButton size="small" color="info" onClick={addLanguageTab}>
                    Add
                  </MDButton>
                  <MDButton
                    size="small"
                    color="secondary"
                    variant="outlined"
                    onClick={() => setExtraLangPicker(false)}
                  >
                    Cancel
                  </MDButton>
                </Stack>
              ) : (
                <MDButton
                  size="small"
                  color="info"
                  variant="outlined"
                  onClick={() => setExtraLangPicker(true)}
                >
                  + Add language
                </MDButton>
              )}
            </Stack>

            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ mt: 1, minHeight: 36 }}
            >
              {tabLangs.map((lng) => (
                <Tab
                  key={lng}
                  value={lng}
                  label={
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <span>{lng.toUpperCase()}</span>
                      {lng === baseLang ? (
                        <Chip
                          size="small"
                          label="base"
                          color="primary"
                          sx={{ height: 18, fontSize: 10 }}
                        />
                      ) : null}
                    </Stack>
                  }
                  sx={{ minHeight: 36, textTransform: "none", fontWeight: 600 }}
                />
              ))}
            </Tabs>

            <Stack spacing={2} sx={{ mt: 2 }}>
              <TextField
                fullWidth
                size="small"
                label={`Package title (${activeTab.toUpperCase()})`}
                value={currentLocal.package_title || ""}
                onChange={(e) =>
                  updateLocalizedField(activeTab, "package_title", e.target.value)
                }
                helperText="Shown above the package name on the paywall."
              />
              <TextField
                fullWidth
                size="small"
                label={`Name (${activeTab.toUpperCase()})`}
                value={currentLocal.name || ""}
                onChange={(e) =>
                  updateLocalizedField(activeTab, "name", e.target.value)
                }
              />
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={4}
                label={`Description (${activeTab.toUpperCase()})`}
                value={currentLocal.description || ""}
                onChange={(e) =>
                  updateLocalizedField(activeTab, "description", e.target.value)
                }
                helperText='HTML allowed. Use "${checkImg}" / "${redCross}" for the check / cross icons (same convention as the mobile app).'
              />
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <MDButton
          variant="outlined"
          color="secondary"
          onClick={onClose}
          disabled={saving}
        >
          Cancel
        </MDButton>
        <MDButton
          variant="contained"
          color="info"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Saving…" : "Save changes"}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
